import { useCallback, useEffect, useRef } from 'react';
import { AppText, Button, Row, ToastTypes } from '@streem/ui-react';
import { createListGroupsStore } from './list_store_hooks';
import { useActiveCompanyCode } from './use_active_company_code';
import { useUIStore } from './use_global_context';
import { autorun } from 'mobx';
import { useUserAuthenticatedAndSettingsLoaded } from './use_user_authenticated_and_settings_loaded';

export function useGroupsStore(): ReturnType<typeof createListGroupsStore> {
    const groupsStore = createListGroupsStore();
    const companyCode = useActiveCompanyCode();
    const uiStore = useUIStore();
    const toastDisplayed = useRef(false);
    const userAuthenticatedAndSettingsLoaded = useUserAuthenticatedAndSettingsLoaded();

    useEffect(() => {
        if (userAuthenticatedAndSettingsLoaded) {
            const fetchPromise = groupsStore.fetch(companyCode);
            return function () {
                fetchPromise.cancel();
            };
        }
    }, [groupsStore, companyCode, userAuthenticatedAndSettingsLoaded]);

    const tryAgain = useCallback(async () => {
        groupsStore.fetch(companyCode);
    }, [groupsStore, companyCode]);

    useEffect(() => {
        const id = 'load-groups-failed';
        const disposer = autorun(() => {
            if (groupsStore.lastError != null && !toastDisplayed.current) {
                toastDisplayed.current = true;
                uiStore.addToastWithManualClose({
                    id,
                    content: <ToastContent tryAgainHandler={tryAgain} />,
                    flavor: ToastTypes.ERROR,
                });
            } else if (groupsStore.lastError == null && toastDisplayed.current) {
                uiStore.removeToast(id);
                toastDisplayed.current = false;
            }
        });
        return disposer;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiStore, tryAgain]); // groupStore.lastError is a mobx observable

    return groupsStore;
}

const BUTTON_WIDTH = '5rem';
const ToastContent = ({ tryAgainHandler }: { tryAgainHandler: () => Promise<void> }) => {
    return (
        <Row alignItems="center" justifyContent="space-between">
            <AppText style={{ flexGrow: 1, padding: 0 }}>
                Unexpected error, call transfer button will not be available
            </AppText>
            <Button
                fontColor="azure"
                variant="transparent"
                onClick={tryAgainHandler}
                style={{ flexGrow: 0, flexShrink: 0, padding: 0, width: BUTTON_WIDTH }}
            >
                <span style={{ textDecoration: 'underline' }}>Try Again</span>
            </Button>
        </Row>
    );
};
